import './underwriting.submission.detail.overview.scss';

import * as Yup from 'yup';

import {
  ActionType,
  DeclineSubmissionButton,
  LostSubmissionButton,
  ReferredSubmissionButton,
  UndoLostSubmissionButton,
  UpdateSubmissionStatusButton,
} from '../submission-action';
import {
  EventNoticeDagIdEnum,
  NavigationItem,
  NoteTargetAreaType,
  StatusTypeEnum,
  StatusValue,
  Submission,
  SubmissionDetailData,
  SubmissionDetailInsuredEntityAliasData,
  SubmissionDetailUserData,
  SubmissionStatus,
  SubmissionSubStatus,
  TargetAreaType,
  TargetType,
  Task,
  TaskEdges,
  TaskLabel,
  TaskStatus,
  UserGroup,
  getFirstPaginatedNode,
  statusValueToStatus,
  useGetNotes,
  useGetUsers,
  useShouldPoll,
  useStatusesQuery,
  useStatusesSubscription,
  useUnderwritingEvaluationMutation,
  useUpsertSubmissionMutation,
  useUpsertTaskMutation,
} from '@converge/graphql';
import {
  Box,
  IconButton,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CircularLoader,
  ErrorPageLayout,
  FormContainerWithHeader,
  FormDateField,
  FormFieldContainer,
  LazyLoadSection,
  NewConvergeButton,
  NewFromSelectField,
  PaperLayout,
  StyledConvergeFormTableCell,
  StyledConvergeFormTableContainer,
} from '../../../presentation';
import { CreateSubmissionField } from '@converge/data';
import {
  EditSubmissionButton,
  IEditSubmissionButtonProps,
} from '../edit-submission';
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form';
import { IEntityOverviewFormData } from '../../entity/insured/overview/insured-entity.detail.overview';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  SpeakerNotes,
  SpeakerNotesOff,
  SpeakerNotesOutlined,
} from '@mui/icons-material';
import {
  UnderwritingOverviewBrokerInfoFormSection,
  UnderwritingOverviewClearanceFormSection,
  UnderwritingOverviewPolicyHolderInfoFormSection,
  UnderwritingOverviewRequestedPolicySection,
  UnderwritingOverviewSubmissionNotesFormSection,
  UnderwritingOverviewUnderwritingAttachmentsFormSection,
  UnderwritingOverviewUnderwritingCommentsFormSection,
} from './form-sections';
import {
  createSubmissionFieldsSchema,
  insuredEntity,
  stringRequired,
} from './underwriting.submission.detail.validation.schema';
import { displayDateFromNow, getTaskLabel } from '@converge/utils';
import { useConvergeNavigation, useConvergeSnackBar } from '../../../contexts';
import { useOutletContext, useParams, useLocation } from 'react-router-dom';

import { ApolloError } from '@apollo/client';
import { ConvergeNotes } from '../../notes';
import { DocumentButton } from '../submission-action/document-button';
import { HoldSubmissionButton } from '../submission-action/hold-submission-button';
import { ReopenSubmissionButton } from '../submission-action/reopen-submission-button';
import { UnderwritingOverviewDataCollectionSection } from './underwriting.submission.data-collection.section';
import { UnderwritingOverviewUnderwritingDescriptionOfOperations } from './form-sections/underwriting.overview.underwriting-description-of-operations';
import { UnderwritingOverviewUnderwritingProsCons } from './form-sections/underwriting.overview.underwriting-pros-cons';
import { UnderwritingOverviewUnderwritingRulesSection } from './underwriting.submission.underwriting-rules.section';
import { UnderwritingSubmissionDetailOverviewStatusAlert } from './underwriting.submission.detail.overview.status.alert';
import { UnderwritingSubmissionPageContainer } from '../underwriting.submission.page.container';
import config from '@converge/config';
import { convergeLogger } from '@converge/config';
import debounce from 'lodash.debounce';
import { getUserOptions } from '../../submission';
import moment from 'moment';
import { useAuthContext } from '../../auth/require-auth';
import { useUnderwritingSubmissionStateContext } from '../../underwriting/underwriting.submission.context';
import { yupResolver } from '@hookform/resolvers/yup';
import { mapToAPIOverviewModel, mapToUIOverviewInsuredModel } from './mapper';

const TASK_STATUS_OPTIONS = [
  {
    label: 'To do',
    value: TaskStatus.ToDo,
  },
  {
    label: 'In progress',
    value: TaskStatus.InProgress,
  },
  {
    label: 'Done',
    value: TaskStatus.Done,
  },
  {
    label: 'Cancelled',
    value: TaskStatus.Cancelled,
  },
];
type Nullable<T> = T | null | undefined;

interface IEntityAddress {
  uuid: Nullable<string>;
  street1: Nullable<string>;
  street2: Nullable<string>;
  city: Nullable<string>;
  region: Nullable<string>;
  regionName: Nullable<string>;
  postalCode: Nullable<string>;
  country: Nullable<string>;
  countryName: Nullable<string>;
  iso2Code: Nullable<string>;
  source: Nullable<string>;
}

export interface IUIUnderwritingOverviewModel extends IEntityOverviewFormData {
  uuid: Nullable<string>;
  status: Nullable<SubmissionStatus>;
  brokerCommission: Nullable<number>;
  aggregateLimit: Nullable<number>;
  retention: Nullable<number>;
  type: Nullable<string>;

  programUuid: Nullable<string>;
  createdDate: Nullable<string>;
  effectiveDate: Nullable<string>;
  expirationDate: Nullable<string>;
  notes: Nullable<string>;
  brokerEntity: {
    uuid: Nullable<string>;
    primaryAlias: Nullable<SubmissionDetailInsuredEntityAliasData>;
    primaryAddress: Nullable<IEntityAddress>;
    primaryContact: {
      uuid: Nullable<string>;
      firstName: Nullable<string>;
      lastName: Nullable<string>;
      email: Nullable<string>;
      phone: Nullable<string>;
    };
  };
  policyFee: Nullable<number>;
  product: {
    value: string | null | undefined;
    label: string | null | undefined;
  };
  policyFundamentals: Nullable<{
    uuid: Nullable<string>;
    revenue: Nullable<number>;
    employees: Nullable<number>;
    projectedRevenue: Nullable<number>;
    ratedRevenueOverride: Nullable<number>;
    foreignRevenue: Nullable<number>;
    projectedForeignRevenue: Nullable<number>;
    currentRatio: Nullable<number>;
    yearEstablished: Nullable<number>;
    quickRatio: Nullable<number>;
    cashRatio: Nullable<number>;
    sources: Nullable<string[]>;
    primary: Nullable<boolean>;
  }>;
  descriptionOfOperations: Nullable<string>;
  prosAndCons: Nullable<string>;
  propertyAndCasualtyLicenseUuid: Nullable<string>;
  surplusLinesTaxLicenseUuid: Nullable<string>;
  financialAnalysisSummary: Nullable<string>;
  program: {
    value: string | null | undefined;
    label: string | null | undefined;
  };
  partner: {
    value: string | null | undefined;
    label: string | null | undefined;
  };
}

export interface ISubmissionProps {
  submission: SubmissionDetailData;
  firstSubLeftNavItem: NavigationItem;
  isRenewalSubmission: boolean;
  isScoringDone: boolean;
  submissionData: Submission;
  underwriter?: SubmissionDetailUserData | null;
  insuredEntityName?: string | null;
  loading: boolean;
  error: ApolloError;
}

export const UnderwritingSubmissionDetailOverview = () => {
  const {
    submissionData: submission,
    loading,
    error,
  } = useOutletContext<ISubmissionProps>();
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  if (loading && !submission) {
    return <CircularLoader className="circular-loader-style" />;
  }

  if (error) {
    return <ErrorPageLayout message={error.message} messageId="todo" />;
  }

  if (!submission) {
    return <ErrorPageLayout message="No submission found" messageId="todo" />;
  }

  return (
    <UnderwritingSubmissionDetailOverviewInner
      data={submission}
      showUploadDialog={showUploadDialog}
      setShowUploadDialog={setShowUploadDialog}
    />
  );
};

const yupString = stringRequired(CreateSubmissionField.RegionUuid);

export const isHoldStatus = (submission: SubmissionDetailData) =>
  (submission?.status &&
    [
      SubmissionStatus.Hold,
      SubmissionStatus.Cancelled,
      SubmissionStatus.PendingCancellation,
    ].includes(submission.status)) ||
  false;

interface EditButtonProps extends IEditSubmissionButtonProps {
  isEditButtonEnabled: boolean;
  holdStatus: boolean;
}

export const EditButton = ({
  isEditButtonEnabled,
  holdStatus,
  submissionUuid,
  pageType,
}: EditButtonProps) => {
  return (
    isEditButtonEnabled &&
    !holdStatus && (
      <Box className="button-box first-button-box">
        <EditSubmissionButton
          submissionUuid={submissionUuid}
          pageType={pageType}
        />
      </Box>
    )
  );
};

interface ScoreRateProps extends IScoreAndRateButtonProps {
  isScoreAndRateButtonEnabled: boolean;
  holdStatus: boolean;
}
export const ScoreRateButton = ({
  isScoreAndRateButtonEnabled,
  holdStatus,
  disabled,
  loading,
  onClick,
  refetchStatus,
  updateRefetchStatus,
}: ScoreRateProps) => {
  return (
    <>
      {isScoreAndRateButtonEnabled && !holdStatus && (
        <Box className="button-box first-button-box">
          <ScoreAndRateButton
            onClick={onClick}
            loading={loading}
            disabled={disabled}
            refetchStatus={refetchStatus}
            updateRefetchStatus={updateRefetchStatus}
          />
        </Box>
      )}
    </>
  );
};

type SharedFooterButtonsProps = {
  submission: SubmissionDetailData;
  holdStatus: boolean;
  onOpenUploadDialog?: () => void;
};

export const SharedFooterButtons = ({
  submission,
  holdStatus,
  onOpenUploadDialog,
}: SharedFooterButtonsProps) => {
  const isShowLostButton =
    submission.status &&
    [
      SubmissionStatus.Closed,
      SubmissionStatus.Evaluation,
      SubmissionStatus.Quoted,
      SubmissionStatus.Referred,
      SubmissionStatus.Submitted,
      SubmissionStatus.Cleared,
    ].includes(submission.status);

  const showHoldButton =
    submission.status &&
    ([SubmissionStatus.Hold, SubmissionStatus.Cleared].includes(
      submission.status
    ) ||
      (submission.status === SubmissionStatus.Evaluation &&
        submission.subStatus === SubmissionSubStatus.EvaluationEditable));

  return (
    <>
      {submission.status === SubmissionStatus.Closed && (
        <Box className="button-box">
          <ReopenSubmissionButton
            referenceId={submission?.referenceId ?? ''}
            submissionId={submission?.uuid}
          />
        </Box>
      )}
      {!holdStatus && (
        <Box className="button-box">
          <DeclineSubmissionButton
            submissionId={submission?.uuid}
            referenceId={submission?.referenceId ?? ''}
          />
        </Box>
      )}
      {isShowLostButton && (
        <Box className="button-box">
          <LostSubmissionButton
            submissionId={submission?.uuid}
            referenceId={submission?.referenceId ?? ''}
          />
        </Box>
      )}

      {submission.status === SubmissionStatus.Lost && !holdStatus && (
        <Box className="button-box">
          <UndoLostSubmissionButton
            referenceId={submission?.referenceId ?? ''}
            submissionId={submission?.uuid}
          />
        </Box>
      )}
      {submission.status !== SubmissionStatus.Declined && !holdStatus && (
        <Box className="button-box">
          <ReferredSubmissionButton
            referenceId={submission?.referenceId ?? ''}
            submissionId={submission?.uuid}
          />
        </Box>
      )}
      {onOpenUploadDialog && (
        <Box sx={{ marginRight: '10px' }}>
          <NewConvergeButton
            startIcon={
              <FileDownloadOutlinedIcon
                className="doc-folder"
                sx={{ transform: 'rotate(180deg)' }}
              />
            }
            label={'Upload'}
            onClick={onOpenUploadDialog}
          />
        </Box>
      )}
      {submission.documentLink && !holdStatus && (
        <Box className="button-box" style={{ marginLeft: '0px' }}>
          <DocumentButton submissionDocumentLink={submission.documentLink} />
        </Box>
      )}
      {showHoldButton && (
        <Box className="button-box">
          <HoldSubmissionButton
            referenceId={submission?.referenceId ?? ''}
            submissionId={submission?.uuid}
          />
        </Box>
      )}
    </>
  );
};
export const UnderwritingSubmissionDetailOverviewInner = ({
  data,
  setShowUploadDialog,
  showUploadDialog,
}: {
  data: SubmissionDetailData;
  underwriter?: SubmissionDetailUserData | null;
  insuredEntityName?: string | null | undefined;
  showUploadDialog: boolean;
  setShowUploadDialog: Dispatch<SetStateAction<boolean>>;
}) => {
  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { submission, firstSubLeftNavItem, isRenewalSubmission } =
    useOutletContext<ISubmissionProps>();
  const navigate = useConvergeNavigation();
  const location = useLocation();
  const { upsertSubmission, loading: upsertSubmissionIsLoading } =
    useUpsertSubmissionMutation({}, 'Overview');
  const { updateStatus } = useStatusesSubscription();
  const [refetchStatus, setRefetchStatus] = useState(false);
  const countryValidationBlockedRef = useRef(false);
  const attachmentsSectionRef = useRef<HTMLDivElement>(null);
  const handleOpenUploadDialog = () => {
    setShowUploadDialog(true);
  };

  useEffect(() => {
    if (showUploadDialog) {
      attachmentsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showUploadDialog]);
  const {
    isEditButtonEnabled,
    isReadOnly,
    isQuoted,
    isScoreAndRateButtonEnabled,
    info: { boundQuote },
  } = useUnderwritingSubmissionStateContext();
  useEffect(() => {
    if (
      updateStatus?.noticeArea === 'DAG' &&
      updateStatus?.noticeValue === 'completed'
    )
      setRefetchStatus(true);
  }, [updateStatus]);

  const {
    createUnderwritingEvaluation,
    loading: underwritingEvaluationLoading,
  } = useUnderwritingEvaluationMutation();

  const UnderwritingEvaluationHandler = async (submissionUuid: string) => {
    try {
      await createUnderwritingEvaluation({
        variables: {
          submissionUuid,
        },
      });

      notify({
        message: 'Underwriting evaluation created successfully',
        variant: 'success',
      });
    } catch (error) {
      convergeLogger.error(error);
      notify(`Failed to generate underwriting evaluation`, {
        variant: 'error',
      });
    }
  };

  const policyFundamentals =
    boundQuote?.policies?.edges[0].node?.fundamentals.edges.filter(
      (fundamental) => {
        return fundamental.node.primary;
      }
    );

  const defaultValues = useMemo(
    () => mapToUIOverviewInsuredModel(data, policyFundamentals),
    [data, policyFundamentals]
  );
  const policyDatesErrorMessage =
    'Policy end date must be at least 1 month after the policy start date.';
  const formMethods = useForm<IUIUnderwritingOverviewModel>({
    resolver: yupResolver(
      createSubmissionFieldsSchema.concat(
        Yup.object().shape({
          insuredEntity: insuredEntity.concat(
            Yup.object({
              primaryAddress: Yup.object({
                region: yupString.uuid(),
                street1: stringRequired(CreateSubmissionField.Street1),
                country: stringRequired(CreateSubmissionField.CountryUuid),
                city: stringRequired(CreateSubmissionField.City),
                postalCode: stringRequired(CreateSubmissionField.PostalCode),
              }),
            })
          ),
        })
      )
    ),
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const {
    trigger,
    formState: { errors },
  } = formMethods;

  const { isDirty, dirtyFields } = useFormState<IUIUnderwritingOverviewModel>({
    control: formMethods.control,
  });

  const watchedAddressCountry = useWatch({
    control: formMethods.control,
    name: 'insuredEntity.primaryAddress.country',
  });
  const watchedAddressRegion = useWatch({
    control: formMethods.control,
    name: 'insuredEntity.primaryAddress.region',
  });

  useEffect(() => {
    countryValidationBlockedRef.current = false;
  }, [watchedAddressCountry, watchedAddressRegion]);
  const expirationDate = formMethods.getValues('expirationDate');
  const checkPolicyDates = useCallback(() => {
    const effectiveDate = formMethods.getValues('effectiveDate');
    const expirationDate = formMethods.getValues('expirationDate');

    formMethods?.clearErrors('effectiveDate');
    if (
      effectiveDate &&
      expirationDate &&
      moment(expirationDate).isBefore(moment(effectiveDate).add(1, 'month'))
    ) {
      formMethods?.setError('expirationDate', {
        type: 'manual',
        message: policyDatesErrorMessage,
      });
      return false;
    }

    return true;
  }, [formMethods]);
  const onSubmitHandler = async (uiData: IUIUnderwritingOverviewModel) => {
    const dirtyObjectKeys = listObjectKeys(dirtyFields);

    if (dirtyObjectKeys.length === 0 || !isDirty) {
      navigateToQuestionnaire();
      return;
    }

    if (!checkPolicyDates()) {
      return;
    }

    // Prevent manual save that could trigger scoring if ratable revenue is invalid
    if (!validateRatableRevenue(uiData)) {
      notify('Cannot save: Ratable revenue is required before saving changes', {
        variant: 'error',
      });
      return;
    }

    closeSnackbar();
    notify('Saving...', {
      variant: 'info',
    });
    const submissionInput = mapToAPIOverviewModel(uiData, dirtyFields);

    convergeLogger.info({
      message: `upsertSubmission is running because of these fields: ${dirtyObjectKeys.join(
        ', '
      )}`,
    });

    try {
      await upsertSubmission({
        variables: {
          details: submissionInput,
        },
      });
      closeSnackbar();
      notify('Saved successfully', {
        variant: 'success',
      });
      navigateToQuestionnaire();
    } catch (e) {
      closeSnackbar();
      notify(
        `Error saving: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const scrollToRatableRevenueField = useCallback(() => {
    const ratableRevenueField = document.querySelector(
      'input[name="insuredEntity.primaryFundamentals.ratedRevenueOverride"]'
    ) as HTMLElement;

    if (ratableRevenueField) {
      ratableRevenueField.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      ratableRevenueField.focus();
    }
  }, []);

  // Validation function that works with raw submission data
  const validateRatableRevenueFromData = useCallback(
    (submissionData: SubmissionDetailData): boolean => {
      const primaryFundamentals =
        submissionData?.insuredEntity?.node?.fundamentals?.edges?.find(
          (edge) => edge.node.primary
        )?.node;
      const ratableRevenue = primaryFundamentals?.ratedRevenueOverride;
      return ratableRevenue != null && Number(ratableRevenue) > 0;
    },
    []
  );

  const validateRatableRevenue = useCallback(
    (uiData: IUIUnderwritingOverviewModel): boolean => {
      const ratableRevenue =
        uiData?.insuredEntity?.primaryFundamentals?.ratedRevenueOverride;
      const isRatableRevenueInvalid =
        !ratableRevenue || Number(ratableRevenue) <= 0;

      if (isRatableRevenueInvalid) {
        formMethods.setError(
          'insuredEntity.primaryFundamentals.ratedRevenueOverride',
          {
            type: 'manual',
            message: 'This field is required and must be more than 0',
          }
        );
        scrollToRatableRevenueField();
        return false;
      }

      // Clear any manual errors on the ratable revenue field if validation passes
      formMethods.clearErrors(
        'insuredEntity.primaryFundamentals.ratedRevenueOverride'
      );
      return true;
    },
    [formMethods, scrollToRatableRevenueField]
  );

  // Helper function to check if all current errors are only ratable revenue related
  const hasOnlyRatableRevenueErrors = useCallback(
    (errorsObj: Record<string, unknown>): boolean => {
      const errorKeys = Object.keys(errorsObj);

      if (errorKeys.length === 0) {
        return true;
      }

      const hasNonRatableErrors = errorKeys.some((key) => {
        if (key === 'insuredEntity') {
          const insuredEntityErrors = errorsObj[key];

          if (
            (insuredEntityErrors as Record<string, unknown>)?.[
              'primaryFundamentals'
            ]
          ) {
            const fundamentalErrors = (
              insuredEntityErrors as Record<string, unknown>
            )['primaryFundamentals'];

            const nonRatableKeys = Object.keys(
              fundamentalErrors as Record<string, unknown>
            ).filter((fundKey) => fundKey !== 'ratedRevenueOverride');

            if (nonRatableKeys.length > 0) {
              return true;
            }
          }

          const nonFundamentalKeys = Object.keys(
            insuredEntityErrors as Record<string, unknown>
          ).filter((entityKey) => entityKey !== 'primaryFundamentals');

          return nonFundamentalKeys.length > 0;
        }

        return true;
      });

      return !hasNonRatableErrors;
    },
    []
  );

  const handleDebouncedAutoSave = useMemo(
    () =>
      debounce(
        async (uiData: IUIUnderwritingOverviewModel) => {
          if (countryValidationBlockedRef.current) {
            return;
          }

          const dirtyObjectKeys = listObjectKeys(dirtyFields);

          if (dirtyObjectKeys.length === 0) {
            return;
          }

          if (!checkPolicyDates()) {
            return;
          }
          const isRatableRevenueCurrentlyValid = validateRatableRevenue(uiData);

          if (!isRatableRevenueCurrentlyValid) {
            convergeLogger.info({
              message:
                'Skipping auto-save: Ratable revenue is invalid, preventing potential auto-scoring',
            });
            return;
          }

          const currentErrorKeys = Object.keys(errors);
          const hasStaleRatableRevenueError = currentErrorKeys.some((key) =>
            key.includes('ratedRevenueOverride')
          );

          if (hasStaleRatableRevenueError) {
            formMethods.clearErrors(
              'insuredEntity.primaryFundamentals.ratedRevenueOverride'
            );
          }

          closeSnackbar();

          if (!hasOnlyRatableRevenueErrors(errors)) {
            return;
          }

          convergeLogger.info({
            message: `Upserting submission for these changed fields during auto save: ${dirtyObjectKeys.join(
              ', '
            )}`,
          });

          notify('Saving...', {
            variant: 'info',
          });
          const submissionInput = mapToAPIOverviewModel(uiData, dirtyFields);
          try {
            const response = await upsertSubmission({
              variables: {
                details: submissionInput,
              },
            });
            closeSnackbar();

            if (
              response.data?.upsertSubmission.__typename === 'ValidationErrors'
            ) {
              notify('Saved failed', {
                variant: 'error',
              });
            } else {
              notify('Saved successfully', {
                variant: 'success',
              });
            }

            formMethods.reset({}, { keepValues: true });
          } catch (e) {
            closeSnackbar();
            const msg = e instanceof Error ? e.message : 'Unknown error';
            if (
              msg.includes('are not allowed') ||
              msg.includes('does not belong to country')
            ) {
              countryValidationBlockedRef.current = true;
            }
            notify(`Error saving: ${msg}`, {
              variant: 'error',
            });
          }
          trigger();
        },
        3000,
        {
          leading: false,
          trailing: true,
        }
      ),
    [
      dirtyFields,
      formMethods,
      notify,
      errors,
      trigger,
      closeSnackbar,
      upsertSubmission,
      checkPolicyDates,
      validateRatableRevenue,
      hasOnlyRatableRevenueErrors,
    ]
  );

  const navigateToQuestionnaire = useCallback(() => {
    const application = getFirstPaginatedNode(submission?.applications);
    navigate.underwritingSubmissionDetail({
      submissionUuid: submission.uuid,
      applicationUuid: application?.uuid,
      submissionApplicationUuid: application?.submissionApplication.node?.uuid,
      sectionUrl: 'questionnaire',
      subSectionUrl: firstSubLeftNavItem?.value,
    });
  }, [submission, firstSubLeftNavItem, navigate]);

  const navigateToOverview = useCallback(() => {
    const application = getFirstPaginatedNode(submission?.applications);
    navigate.underwritingSubmissionDetail({
      submissionUuid: submission.uuid,
      applicationUuid: application?.uuid,
      submissionApplicationUuid: application?.submissionApplication.node?.uuid,
      sectionUrl: 'overview',
    });
  }, [submission, navigate]);

  const { upsertTask } = useUpsertTaskMutation();

  useEffect(() => {
    handleDebouncedAutoSave.cancel();
    const dirtyObjectKeys = listObjectKeys(dirtyFields);

    let isDirtyActiveFields = true;
    const activeFields = [
      'descriptionOfOperations',
      'financialAnalysisSummary',
      'prosAndCons',
      'notes',
    ];
    const formValues = formMethods.getValues();
    activeFields.forEach((field) => {
      if (
        dirtyObjectKeys.includes(field) &&
        !formValues[field as keyof IUIUnderwritingOverviewModel]
      ) {
        isDirtyActiveFields = false;
      }
    });

    if (!checkPolicyDates()) {
      return;
    }

    // Allow initial autosave if only ratable revenue errors exist
    if (isDirty && hasOnlyRatableRevenueErrors(errors) && isDirtyActiveFields) {
      handleDebouncedAutoSave(formMethods.getValues());
    }

    const subscription = formMethods.watch((value) => {
      // Allow watch-triggered autosave if only ratable revenue errors exist
      if (!isDirty || !hasOnlyRatableRevenueErrors(errors)) {
        checkPolicyDates();
        return;
      }

      handleDebouncedAutoSave(value as IUIUnderwritingOverviewModel);
    });

    return () => subscription.unsubscribe();
  }, [
    isDirty,
    formMethods,
    handleDebouncedAutoSave,
    errors,
    notify,
    closeSnackbar,
    dirtyFields,
    upsertTask,
    submission.uuid,
    expirationDate,
    checkPolicyDates,
    hasOnlyRatableRevenueErrors,
  ]);

  const onEvaluateSubmissionEvent = async (submissionUuid: string) => {
    const isOnOverviewPage = location.pathname.includes('/overview');

    // Choose validation method based on current page
    const isValidRatableRevenue = isOnOverviewPage
      ? validateRatableRevenue(formMethods.getValues())
      : validateRatableRevenueFromData(data);

    if (!isValidRatableRevenue) {
      if (isOnOverviewPage) {
        // If we're on the Overview page, show the error and scroll to field
        notify('Ratable Revenue field is required on the Overview page', {
          variant: 'error',
          autoHideDuration: null,
        });
      } else {
        // If we're on a different page, navigate to Overview and show error
        navigateToOverview();
        // Add a delay to ensure navigation completes before showing notification
        setTimeout(() => {
          notify('Ratable Revenue field is required on the Overview page', {
            variant: 'error',
            autoHideDuration: null,
          });
        }, 100);
      }
      return;
    }

    try {
      await upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid ?? '',
            status: SubmissionStatus.Evaluation,
            subStatus: SubmissionSubStatus.EvaluationNotEditable,
          },
        },
      });
    } catch (cause) {
      closeSnackbar();
      notify(
        cause instanceof Error
          ? `Error saving: ${cause.message}`
          : 'Error saving',
        {
          variant: 'error',
        }
      );
    }
  };

  const hideRequestMoreInfoStatusButton =
    submission?.status &&
    [
      SubmissionStatus.Quoted,
      SubmissionStatus.Bound,
      SubmissionStatus.PolicyIssued,
      SubmissionStatus.Lost,
      SubmissionStatus.Declined,
      SubmissionStatus.RequestedMoreInfo,
    ].includes(submission.status);
  const hideIndicateStatusButton =
    submission?.status &&
    [
      SubmissionStatus.Quoted,
      SubmissionStatus.Bound,
      SubmissionStatus.PolicyIssued,
      SubmissionStatus.Lost,
      SubmissionStatus.Declined,
      SubmissionStatus.Indicated,
    ].includes(submission.status);
  const holdStatus = isHoldStatus(submission);
  const updateRefetchStatus = () => setRefetchStatus(false);
  interface TransformedCapacityProvider {
    uuid: string;
    name: string | null;
    quotaShare: number;
  }
  // Transform the GraphQL response to IQuotaShare array
  const transformCapacityProviders = (
    capacityProviders: typeof data.capacityProviders
  ): TransformedCapacityProvider[] => {
    if (!capacityProviders?.edges) {
      return [];
    }

    return capacityProviders.edges.map((edge) => ({
      uuid: edge.node.uuid,
      name: edge.node.aliases?.edges?.[0]?.node?.name || null,
      quotaShare: edge.node.quote?.quotaShare || 0,
    }));
  };

  return (
    <>
      <UnderwritingSubmissionPageContainer
        title="Overview"
        onNext={() => onSubmitHandler(formMethods.getValues())}
        footerLeft={
          <Stack className="overview-footer">
            {EditButton({
              isEditButtonEnabled: isEditButtonEnabled || false,
              submissionUuid: submission.uuid,
              holdStatus,
              pageType: 'Overview',
            })}
            {ScoreRateButton({
              isScoreAndRateButtonEnabled: isScoreAndRateButtonEnabled || false,
              holdStatus,
              loading: upsertSubmissionIsLoading,
              onClick: () => onEvaluateSubmissionEvent(data.uuid),
              disabled: (() => {
                // Only disable for validation errors other than ratable revenue
                const errorKeys = Object.keys(errors);
                const hasNonRatableRevenueErrors = errorKeys.some(
                  (key) => !key.includes('ratedRevenueOverride')
                );
                return hasNonRatableRevenueErrors;
              })(),
              refetchStatus,
              updateRefetchStatus,
            })}
            {!hideIndicateStatusButton && !holdStatus && (
              <Box className="button-box">
                <UpdateSubmissionStatusButton
                  submissionUuid={submission?.uuid}
                  type={ActionType.INDICATE}
                  label="Indicate"
                  title="Indicate submission"
                />
              </Box>
            )}
            {!hideRequestMoreInfoStatusButton && !holdStatus && (
              <Box className="button-box">
                <UpdateSubmissionStatusButton
                  submissionUuid={submission?.uuid}
                  type={ActionType.REQUEST_MORE_INFO}
                  label="Request more info"
                />
              </Box>
            )}
            {SharedFooterButtons({
              submission,
              holdStatus,
              onOpenUploadDialog: handleOpenUploadDialog,
            })}
            {(data.status === SubmissionStatus.Quoted ||
              data.status === SubmissionStatus.Bound ||
              data.status === SubmissionStatus.PolicyIssued) && (
              <Box className="button-box">
                <NewConvergeButton
                  onClick={() => {
                    UnderwritingEvaluationHandler(data.uuid);
                  }}
                  loading={underwritingEvaluationLoading}
                  label={'Create uw evaluation'}
                />
              </Box>
            )}
          </Stack>
        }
      >
        <PaperLayout className="underwriting-details-form-layout">
          <FormProvider {...formMethods}>
            <div className="form-wrapper">
              <UnderwritingSubmissionDetailOverviewStatusAlert />
              {isRenewalSubmission && (
                <FormContainerWithHeader title="Renewal Tasks">
                  <FormFieldContainer>
                    <StyledConvergeFormTableContainer>
                      <Table
                        sx={{ minWidth: 650 }}
                        aria-label="status"
                        size="small"
                      >
                        <TableHead>
                          <TableRow sx={{ fontWeight: 'bold' }}>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            >
                              Task
                            </StyledConvergeFormTableCell>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            >
                              Status
                            </StyledConvergeFormTableCell>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            >
                              Task Owner
                            </StyledConvergeFormTableCell>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            >
                              Due Date
                            </StyledConvergeFormTableCell>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            >
                              Countdown
                            </StyledConvergeFormTableCell>
                            <StyledConvergeFormTableCell
                              component="th"
                              scope="row"
                            ></StyledConvergeFormTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TaskList
                            tasks={data?.tasks as TaskEdges}
                            submissionUuid={submission.uuid}
                          />
                        </TableBody>
                      </Table>
                    </StyledConvergeFormTableContainer>
                  </FormFieldContainer>
                </FormContainerWithHeader>
              )}

              <FormContainerWithHeader title="Data Collection">
                <FormFieldContainer>
                  <UnderwritingOverviewDataCollectionSection
                    updateStatus={updateStatus}
                  />
                </FormFieldContainer>
              </FormContainerWithHeader>

              <LazyLoadSection>
                <FormContainerWithHeader title="Automation">
                  <FormFieldContainer>
                    <UnderwritingOverviewUnderwritingRulesSection
                      updateStatus={updateStatus}
                    />
                  </FormFieldContainer>
                </FormContainerWithHeader>
              </LazyLoadSection>
              <LazyLoadSection>
                <UnderwritingOverviewPolicyHolderInfoFormSection
                  isReadonly={isReadOnly}
                  upsertSubmissionIsLoading={upsertSubmissionIsLoading}
                />
              </LazyLoadSection>

              <LazyLoadSection>
                <UnderwritingOverviewUnderwritingDescriptionOfOperations
                  isReadonly={isReadOnly}
                />
              </LazyLoadSection>

              <LazyLoadSection>
                <UnderwritingOverviewUnderwritingProsCons
                  isReadonly={isReadOnly}
                />
              </LazyLoadSection>

              <LazyLoadSection>
                <UnderwritingOverviewBrokerInfoFormSection
                  isReadonly={isReadOnly}
                />
              </LazyLoadSection>

              {config.featureFlags.showClearanceSectionOnSubmissionOverview && (
                <LazyLoadSection>
                  <UnderwritingOverviewClearanceFormSection
                    isReadonly={isReadOnly}
                  />
                </LazyLoadSection>
              )}

              <LazyLoadSection>
                <UnderwritingOverviewRequestedPolicySection
                  isReadonly={isReadOnly}
                  isQuoted={isQuoted}
                  QuotaShare={transformCapacityProviders(
                    data?.capacityProviders
                  )}
                  submissionUuid={data.uuid}
                  isPartnered={!data.program?.node.partnered}
                />
              </LazyLoadSection>

              <LazyLoadSection>
                <UnderwritingOverviewSubmissionNotesFormSection
                  isReadonly={false}
                />
              </LazyLoadSection>

              <LazyLoadSection>
                <UnderwritingOverviewUnderwritingCommentsFormSection
                  isReadonly={false}
                />
              </LazyLoadSection>

              <div ref={attachmentsSectionRef}>
                <LazyLoadSection forceLoad={showUploadDialog}>
                  <UnderwritingOverviewUnderwritingAttachmentsFormSection
                    setShowUploadDialog={setShowUploadDialog}
                    showUploadDialog={showUploadDialog}
                  />
                </LazyLoadSection>
              </div>
            </div>
          </FormProvider>
        </PaperLayout>
      </UnderwritingSubmissionPageContainer>
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function listObjectKeys(obj: Record<string, any>, prefix = ''): string[] {
  return Object.keys(obj).flatMap((key) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      return listObjectKeys(obj[key], prefixedKey);
    } else {
      return prefixedKey;
    }
  });
}

interface IScoreAndRateButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  disabled?: boolean;
  refetchStatus?: boolean;
  updateRefetchStatus?: () => void;
}

export const ScoreAndRateButton = ({
  onClick,
  loading,
  disabled = false,
  refetchStatus,
  updateRefetchStatus,
}: IScoreAndRateButtonProps) => {
  const { isUserIdle } = useAuthContext();
  const { poll, setPoll } = useShouldPoll();
  const { updateStatus } = useStatusesSubscription();
  const { submissionUuid } = useParams<'submissionUuid'>();
  const [enableScoreAndRate, setEnableScoreAndRate] = useState(false);

  const {
    info: { statuses },
    loading: statusesLoading,
    refetch,
  } = useStatusesQuery({
    operationName: 'scoreAndRateButton',
    poll: isUserIdle ? false : poll,
    filters: {
      targetUuid: submissionUuid,
      statusArea: 'SUBMISSION',
      details: false,
      statusType: StatusTypeEnum.Dag,
      targetType: TargetType.Node,
    },
  });

  useEffect(() => {
    if (
      updateStatus?.noticeArea === 'SUBMISSION' &&
      updateStatus?.noticeDagId === EventNoticeDagIdEnum.Submission &&
      updateStatus?.noticeValue === 'completed'
    ) {
      setEnableScoreAndRate(true);
    }
  }, [updateStatus]);

  useEffect(() => {
    if (refetchStatus) {
      refetch();
      updateRefetchStatus && updateRefetchStatus();
    }
  }, [refetchStatus, refetch, updateRefetchStatus]);

  const { hasSubmissionDagFinished, tooltipText, submissionDagStatus } =
    useMemo(() => {
      const undatedStatus = statuses.sort((a, b) =>
        b.updatedAt.localeCompare(a.updatedAt)
      );
      const submissionDagStatus = undatedStatus.find(
        (status) =>
          status.statusType === StatusTypeEnum.Dag &&
          status.statusArea?.toUpperCase() === 'SUBMISSION'
      );

      const submissionDagStatusValue = submissionDagStatus?.statusValue;

      const submissionDagStatusText = statusValueToText(
        submissionDagStatusValue
      );

      const timeAgo = submissionDagStatus?.updatedAt
        ? displayDateFromNow(submissionDagStatus.updatedAt, {
            ifOlderThanShowDate: true,
            showTime: true,
            datePrefix: 'on',
          })
        : '';

      let hasSubmissionDagFinished = false;

      if (enableScoreAndRate) {
        hasSubmissionDagFinished = enableScoreAndRate;
      } else {
        hasSubmissionDagFinished =
          submissionDagStatusValue === StatusValue.Completed;
      }

      let tooltipText = `Data Collection ${submissionDagStatusText}`;

      if (timeAgo) {
        tooltipText = `${tooltipText} ${timeAgo}.`;
      } else {
        tooltipText = `${tooltipText}.`;
      }

      switch (statusValueToStatus(submissionDagStatusValue)) {
        case StatusValue.Completed:
          tooltipText = `${tooltipText} You can now score and rate.`;
          break;
        case StatusValue.Failed:
          tooltipText = `${tooltipText} Please contact support.`;
          break;
        case StatusValue.InProgress:
          tooltipText = `${tooltipText} Please wait to score and rate.`;
      }

      if (!submissionDagStatus) {
        tooltipText = `${tooltipText} Please contact support.`;
      }

      if (poll) {
        tooltipText = `${tooltipText} Checking for new updates every ${30} seconds...`;
      }

      return {
        submissionDagStatus,
        submissionDagStatusText,
        tooltipText: tooltipText,
        timeAgo,
        hasSubmissionDagFinished,
      };
    }, [poll, statuses, enableScoreAndRate]);

  useEffect(() => {
    if (hasSubmissionDagFinished) {
      if (poll === true) {
        setPoll(false);
      }
    } else {
      if (poll === false) {
        setPoll(true);
      }
    }
  }, [hasSubmissionDagFinished, poll, setPoll]);

  return (
    <NewConvergeButton
      startIcon={''}
      loading={loading || statusesLoading}
      variant="contained"
      onClick={onClick}
      label="Score & Rate"
      disabled={disabled || !hasSubmissionDagFinished}
      tooltip={tooltipText}
      onMouseOver={() => {
        convergeLogger.debug({ submissionDagStatus });
      }}
    />
  );
};

const statusValueToText = (
  statusValue?: null | undefined | StatusValue | string
): string => {
  if (!statusValue) {
    return 'has not started';
  }
  switch (statusValueToStatus(statusValue)) {
    case StatusValue.Completed:
      return 'completed';
    case StatusValue.Failed:
      return 'failed';
    case StatusValue.InProgress:
      return 'is in progress as of';
    case StatusValue.Pending:
      return 'is pending';
    case StatusValue.Other:
      return `is in a "${statusValue}" state`;
    default:
      return 'has not started';
  }
};

export const TaskList = ({
  tasks,
  submissionUuid,
}: {
  tasks: TaskEdges;
  submissionUuid?: string;
}) => {
  return (
    <>
      {tasks?.edges?.length ? (
        tasks?.edges
          .map((edge) => edge.node)
          ?.map((task) => (
            <>
              <TaskItem task={task} submissionUuid={submissionUuid} />
            </>
          ))
      ) : (
        <TableRow>
          <StyledConvergeFormTableCell component="th" scope="row" colSpan={3}>
            <Typography color="gray">No tasks found</Typography>
          </StyledConvergeFormTableCell>
        </TableRow>
      )}
    </>
  );
};

const TaskItem = ({
  task,
  submissionUuid,
}: {
  task: Task;
  submissionUuid?: string;
}) => {
  const [isRowOpen, setIsRowOpen] = useState(false);
  const { upsertTask } = useUpsertTaskMutation();
  const { notify, closeSnackbar } = useConvergeSnackBar();
  const [errorMessage, setErrorMessage] = useState('');

  const { hasNotes } = useGetNotes({
    targetUuid: task?.uuid,
    targetArea: NoteTargetAreaType.Task,
  });

  const { data: underwriterAssistantList } = useGetUsers({
    userGroups: [UserGroup.UnderwriterAssistant],
  });

  const UNDERWRITER_ASSISTANT_OPTIONS = useMemo(
    () => getUserOptions(underwriterAssistantList, true) || [],
    [underwriterAssistantList]
  );
  const handleUpdateTaskStatus = async (
    event: SelectChangeEvent<unknown>,
    taskUuid: string
  ) => {
    const taskStatus = event?.target?.value as TaskStatus;

    notify('Saving task status...', {
      variant: 'info',
    });
    try {
      await upsertTask({
        variables: {
          details: {
            taskUuid: taskUuid,
            targetUuid: submissionUuid,
            targetArea: TargetAreaType.Submission,
            status: taskStatus,
          },
        },
      });
      closeSnackbar();
      notify('Task status saved successfully', {
        variant: 'success',
      });
    } catch (e) {
      closeSnackbar();
      notify(
        `Error saving task status: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
        {
          variant: 'error',
        }
      );
    }
  };

  const handleUpdateTaskOwner = async (
    event: SelectChangeEvent<unknown>,
    taskUuid: string
  ) => {
    const userUuid = event?.target?.value as string;
    notify('Saving task owner...', {
      variant: 'info',
    });
    try {
      await upsertTask({
        variables: {
          details: {
            taskUuid: taskUuid,
            targetUuid: submissionUuid,
            targetArea: TargetAreaType.Submission,
            ownerEmail: userUuid,
          },
        },
      });
      closeSnackbar();
      notify('Task owner saved successfully', {
        variant: 'success',
      });
    } catch (e) {
      closeSnackbar();
      notify(
        `Error saving: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const handleUpdateTaskDueDate = async (
    date: Date | null,
    taskUuid: string
  ) => {
    const pattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!pattern.test(moment(date).utc().format('YYYY-MM-DD'))) {
      setErrorMessage('Enter a valid date MM/DD/YYYY');
      return;
    }
    setErrorMessage('');
    notify('Saving task due date...', {
      variant: 'info',
    });

    try {
      await upsertTask({
        variables: {
          details: {
            taskUuid: taskUuid,
            targetUuid: submissionUuid,
            targetArea: TargetAreaType.Submission,
            dueDate: moment(date).utc().format('YYYY-MM-DD'),
          },
        },
      });
      closeSnackbar();
      notify('Task due date saved successfully', {
        variant: 'success',
      });
    } catch (e) {
      closeSnackbar();
      notify(
        `Error saving: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const renderTaskCountdown = (taskDueDate: Date) => {
    const dueDate = moment(taskDueDate).utc();
    const dateCount = dueDate.diff(moment(), 'days');
    return (
      <span className={dateCount && dateCount < 0 ? 'red-color' : ''}>
        {dateCount || dateCount === 0
          ? `${Math.abs(dateCount)} Days ${dateCount < 0 ? 'Overdue' : ''}`
          : '--'}
      </span>
    );
  };
  return (
    <>
      <TableRow
        sx={{
          backgroundColor: isOverDueCountdown(task?.dueDate)
            ? 'rgba(255, 0, 0, 0.05) !important'
            : '#ffffff',
        }}
      >
        <StyledConvergeFormTableCell>
          {getTaskLabel(task?.label as TaskLabel)}
        </StyledConvergeFormTableCell>
        <StyledConvergeFormTableCell>
          <NewFromSelectField
            name=""
            className="status"
            options={TASK_STATUS_OPTIONS}
            value={task.status}
            onChange={(e) => handleUpdateTaskStatus(e, task.uuid)}
          />
        </StyledConvergeFormTableCell>
        <StyledConvergeFormTableCell style={{ minHeight: '200px' }}>
          <NewFromSelectField
            name=""
            className="task-owner"
            options={UNDERWRITER_ASSISTANT_OPTIONS}
            value={task?.user?.node.username}
            onChange={(e) => handleUpdateTaskOwner(e, task.uuid)}
          />
        </StyledConvergeFormTableCell>
        <StyledConvergeFormTableCell>
          <FormDateField
            name=""
            sx={{
              width: '200px',
              marginTop: `${errorMessage ? '20px' : '0px'}`,
            }}
            defaultValue={task.dueDate}
            errorMessage={errorMessage}
            onDateChange={(newValue) =>
              handleUpdateTaskDueDate(newValue, task.uuid)
            }
          />
          <Box className="error-message">{errorMessage}</Box>
        </StyledConvergeFormTableCell>
        <StyledConvergeFormTableCell>
          {renderTaskCountdown(task?.dueDate)}
        </StyledConvergeFormTableCell>
        <StyledConvergeFormTableCell>
          <IconButton onClick={() => setIsRowOpen(!isRowOpen)}>
            {hasNotes && !isRowOpen ? (
              <SpeakerNotes />
            ) : isRowOpen ? (
              <SpeakerNotesOff />
            ) : (
              <SpeakerNotesOutlined className="subjectivities-notes-icon" />
            )}
          </IconButton>
        </StyledConvergeFormTableCell>
      </TableRow>
      {isRowOpen && (
        <TableRow>
          <StyledConvergeFormTableCell component="th" scope="row" colSpan={6}>
            <ConvergeNotes
              title="Renewal instruction notes"
              hideHeader
              targetArea={NoteTargetAreaType.Task}
              targetUuid={task?.uuid as string}
            />
          </StyledConvergeFormTableCell>
        </TableRow>
      )}
    </>
  );
};

const isOverDueCountdown = (taskDueDate: Date) => {
  const dueDate = moment(taskDueDate).utc();
  return dueDate.diff(moment(), 'days') < 0;
};

// Shared validation function that can be used from any page
export const createScoreRateWithValidation = (
  submissionData: SubmissionDetailData,
  navigate: ReturnType<typeof useConvergeNavigation>,
  notify: ReturnType<typeof useConvergeSnackBar>['notify'],
  upsertSubmission: ReturnType<
    typeof useUpsertSubmissionMutation
  >['upsertSubmission'],
  location?: { pathname: string }
) => {
  const validateRatableRevenueFromData = (
    data: SubmissionDetailData
  ): boolean => {
    const primaryFundamentals =
      data?.insuredEntity?.node?.fundamentals?.edges?.find(
        (edge) => edge.node.primary
      )?.node;
    const ratableRevenue = primaryFundamentals?.ratedRevenueOverride;
    return ratableRevenue != null && Number(ratableRevenue) > 0;
  };

  const navigateToOverview = () => {
    const application = getFirstPaginatedNode(submissionData?.applications);
    navigate.underwritingSubmissionDetail({
      submissionUuid: submissionData.uuid,
      applicationUuid: application?.uuid,
      submissionApplicationUuid: application?.submissionApplication.node?.uuid,
      sectionUrl: 'overview',
    });
  };

  return async (submissionUuid: string) => {
    const isOnOverviewPage = location?.pathname.includes('/overview') ?? false;

    if (!validateRatableRevenueFromData(submissionData)) {
      if (!isOnOverviewPage) {
        // If we're not on Overview page, navigate there first
        navigateToOverview();
        // Add a delay to ensure navigation completes before showing notification and scrolling
        setTimeout(() => {
          notify('Ratable Revenue field is required on the Overview page', {
            variant: 'error',
            autoHideDuration: null,
          });
          // Try to scroll to the field after a longer delay to ensure the page is loaded
          setTimeout(() => {
            const ratableRevenueField = document.querySelector(
              'input[name="insuredEntity.primaryFundamentals.ratedRevenueOverride"]'
            ) as HTMLElement;
            if (ratableRevenueField) {
              ratableRevenueField.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
              ratableRevenueField.focus();
            }
          }, 500);
        }, 100);
      } else {
        // If we're already on Overview, just show the error
        notify('Ratable Revenue field is required on the Overview page', {
          variant: 'error',
          autoHideDuration: null,
        });
      }
      return;
    }

    // Validation passed, proceed with score & rate
    try {
      await upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid ?? '',
            status: SubmissionStatus.Evaluation,
            subStatus: SubmissionSubStatus.EvaluationNotEditable,
          },
        },
      });
    } catch (cause) {
      notify(
        cause instanceof Error
          ? `Error saving: ${cause.message}`
          : 'Error saving',
        {
          variant: 'error',
        }
      );
    }
  };
};
